import PropTypes from 'prop-types';
import { useState, useMemo, useRef, useEffect } from 'react';
import GetIcon from '../../utils/Icon';
import ReactPaginate from 'react-paginate';

const ReportTable = ({
  columns,
  data = [],
  loading,
  onRowClick,
  className = '',
  headerClassName = '',
  rowClassName = '',
  emptyMessage = 'No data available',
  loadingMessage = 'Loading...',
  pageSize = 10,
  totalRecords,
  currentPage: initialPage = 1,
  onPageChange,
  showPagination = true,
  showPageInfo = true,
  paginationClassName = 'mt-2 pb-3',
  footer = null,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [scrollPercentage, setScrollPercentage] = useState(0);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);
  const [columnWidths, setColumnWidths] = useState({});
  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState(null);
  const tableContainerRef = useRef(null);
  const scrollbarThumbRef = useRef(null);
  const scrollbarTrackRef = useRef(null);
  const tableRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  console.log(footer,"footer");
  
  // Initialize column widths from props or use defaults
  useEffect(() => {
    const initialWidths = {};
    columns.forEach((col, index) => {
      if (col.width) {
        initialWidths[index] = col.width;
      }
    });
    setColumnWidths(initialWidths);
  }, [columns]);

  const sortedData = useMemo(() => {
    if (!Array.isArray(data)) return [];
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const getValue = (obj) => sortConfig.key.split('.').reduce((o, i) => o?.[i], obj);
      const aValue = getValue(a);
      const bValue = getValue(b);
      if (aValue === bValue) return 0;
      const comparison = aValue > bValue ? 1 : -1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, sortConfig]);

  const paginatedData = useMemo(() => {
    if (!showPagination) return sortedData;
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize, showPagination]);

  // Check for overflow on load and resize
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const checkOverflow = () => {
      const hasOverflow = container.scrollWidth > container.clientWidth;
      setHasHorizontalOverflow(hasOverflow);
    };

    // Initial check
    checkOverflow();

    // Set up observer for future changes
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(container);

    return () => resizeObserver.unobserve(container);
  }, [data, currentPage, columnWidths]);

  // Sync scroll position with scrollbar thumb
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;
      const percentage = maxScroll === 0 ? 0 : (scrollLeft / maxScroll) * 100;
      setScrollPercentage(percentage);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Update scrollbar thumb position and size
  useEffect(() => {
    const container = tableContainerRef.current;
    const track = scrollbarTrackRef.current;
    const thumb = scrollbarThumbRef.current;

    if (container && track && thumb) {
      // Set thumb width proportional to visible content
      const visibleRatio = container.clientWidth / container.scrollWidth;
      thumb.style.width = `${Math.max(visibleRatio * 100, 10)}%`; // Minimum 10% width

      // Set thumb position based on scroll percentage
      const maxScroll = track.clientWidth - thumb.offsetWidth;
      thumb.style.left = `${(scrollPercentage / 100) * maxScroll}px`;
    }
  }, [scrollPercentage, hasHorizontalOverflow, columnWidths]);

  // Setup drag-to-scroll functionality
  useEffect(() => {
    const thumb = scrollbarThumbRef.current;
    const track = scrollbarTrackRef.current;
    const container = tableContainerRef.current;
    if (!thumb || !track || !container || !hasHorizontalOverflow) return;

    let isDragging = false;
    let startX;
    let startLeft;

    const onMouseDown = (e) => {
      isDragging = true;
      startX = e.clientX;
      startLeft = thumb.offsetLeft;
      document.body.style.userSelect = 'none';
      e.preventDefault();
    };

    const onMouseMove = (e) => {
      if (!isDragging) return;

      const dx = e.clientX - startX;
      let newLeft = startLeft + dx;

      // Constrain to track boundaries
      const maxLeft = track.clientWidth - thumb.offsetWidth;
      newLeft = Math.max(0, Math.min(newLeft, maxLeft));

      // Update thumb position
      thumb.style.left = `${newLeft}px`;

      // Calculate and set container scroll position
      const scrollRatio = newLeft / maxLeft;
      container.scrollLeft = scrollRatio * (container.scrollWidth - container.clientWidth);
    };

    const onMouseUp = () => {
      isDragging = false;
      document.body.style.userSelect = '';
    };

    thumb.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      thumb.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [hasHorizontalOverflow]);

  // Setup click-to-scroll on track
  useEffect(() => {
    const track = scrollbarTrackRef.current;
    const thumb = scrollbarThumbRef.current;
    const container = tableContainerRef.current;
    if (!track || !thumb || !container || !hasHorizontalOverflow) return;

    const onClick = (e) => {
      const trackRect = track.getBoundingClientRect();
      const clickX = e.clientX - trackRect.left;
      const thumbWidth = thumb.offsetWidth;

      // Calculate new thumb position (centered on click)
      let newLeft = clickX - thumbWidth / 2;
      const maxLeft = track.clientWidth - thumbWidth;
      newLeft = Math.max(0, Math.min(newLeft, maxLeft));

      // Update thumb position
      thumb.style.left = `${newLeft}px`;

      // Calculate and set container scroll position
      const scrollRatio = newLeft / maxLeft;
      container.scrollLeft = scrollRatio * (container.scrollWidth - container.clientWidth);
    };

    track.addEventListener('click', onClick);
    return () => track.removeEventListener('click', onClick);
  }, [hasHorizontalOverflow]);

  // Column resizing handlers
  const startResizing = (columnIndex, e) => {
    setIsResizing(true);
    setResizingColumn(columnIndex);
    startXRef.current = e.clientX;
    startWidthRef.current = tableRef.current.querySelectorAll('th')[columnIndex].offsetWidth;

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleResize = (e) => {
    if (!isResizing || resizingColumn === null) return;

    const table = tableRef.current;
    const thElements = table.querySelectorAll('th');
    const currentTh = thElements[resizingColumn];

    const dx = e.clientX - startXRef.current;
    const newWidth = Math.max(50, startWidthRef.current + dx); // Minimum width of 50px

    // Update the column width
    setColumnWidths(prev => ({
      ...prev,
      [resizingColumn]: `${newWidth}px`
    }));
  };

  const stopResizing = () => {
    if (!isResizing) return;

    setIsResizing(false);
    setResizingColumn(null);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  // Add event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResize);
      document.addEventListener('mouseup', stopResizing);
    } else {
      document.removeEventListener('mousemove', handleResize);
      document.removeEventListener('mouseup', stopResizing);
    }

    return () => {
      document.removeEventListener('mousemove', handleResize);
      document.removeEventListener('mouseup', stopResizing);
    };
  }, [isResizing]);

  const handleSort = (key, isSortable) => {
    if (!isSortable) return;
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handlePageChange = (selectedPage) => {
    const newPage = selectedPage.selected + 1;
    setCurrentPage(newPage);
    onPageChange && onPageChange(newPage);
  };

  const pageCount = Math.ceil((totalRecords || data.length) / pageSize);
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalRecords || data.length);

  return (
    <div className="report-table-container" style={{height  : '67vh'}}>
      <div
        className={`table-container ${className}`}
        ref={tableContainerRef}
        style={{
          overflowX: 'auto',
          overflowY: 'hidden',
          paddingBottom: '0px',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        <table
          className="table"
          ref={tableRef}
          style={{ minWidth: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}
        >
          <thead className={`thead-dark ${headerClassName}`}>
            <tr>
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={`table-header ${column.sortable ? 'sortable' : ''} ${sortConfig.key === (column.accessor || column.header) ? 'active-sort' : ''}`}
                  onClick={() => handleSort(column.accessor || column.header, column.sortable)}
                  style={{
                    width: columnWidths[index] || column.width || 'auto',
                    borderRight: '1px solid #e0e0e0',
                    borderBottom: '1px solid #e0e0e0',
                    position: 'relative',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                     textAlign: 'left', 
                     paddingLeft: '10px'
                  }}
                >
                  <div style={{ paddingRight: '10px' }}>
                    {column.header}
                    {column.sortable && (
                      <span className="sort-icon ms-1">
                        {sortConfig.key === (column.accessor || column.header) ? (
                          <span style={{ color: '#391F84' }}>
                            {sortConfig.direction === 'asc'
                              ? <GetIcon iconName="FaSortUp" size={16} design="upsort" />
                              : <GetIcon iconName="FaSortDown" size={16} design="downsort" />}
                          </span>
                        ) : (
                          <span style={{ opacity: 0.5 }}>↕</span>
                        )}
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      height: '100%',
                      width: '5px',
                      cursor: 'col-resize',
                      backgroundColor: isResizing && resizingColumn === index ? '#391F84' : 'transparent',
                    }}
                    onMouseDown={(e) => startResizing(index, e)}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="text-center">{loadingMessage}</td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center">{emptyMessage}</td>
              </tr>
            ) : (
              paginatedData.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  onClick={() => onRowClick && onRowClick(row)}
                  className={rowClassName}
                  style={{ cursor: onRowClick ? 'pointer' : 'default', ...(row.style || {}) }}
                >
                  {columns.map((column, colIndex) => (
                    <td key={colIndex}
                      style={{
                        ...(column.style || {}),
                        borderRight: '1px solid #e0e0e0',
                        borderBottom: '1px solid #e0e0e0',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        width: columnWidths[colIndex] || column.width || 'auto',
                        paddingLeft: '10px',
                         textAlign: 'left', 
                      }}
                    >
                      {column.cellRenderer
                        ? column.cellRenderer(row, rowIndex, column)
                        : row[column.accessor]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
          {footer && Array.isArray(footer) && (
            <tfoot>
              <tr>
                {columns.map((_, index) => (
                  <td key={index}
                    style={{
                      borderTop: '1px solid #e0e0e0',
                      padding: '8px',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                       borderRight: '1px solid #e0e0e0',
                        borderBottom: '1px solid #e0e0e0',
                    }}
                  >
                    {footer[index] ?? ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}

        </table>
      </div>

      {showPagination && pageCount > 1 && (
        <div className={`pagination-container ${paginationClassName}`}>
          <div className="container-fluid d-flex justify-content-between align-items-center">
            {showPageInfo && (
              <p style={{ color: "#412889", fontSize: "0.8rem" }}>
                Showing {startItem} to {endItem} of {totalRecords || data.length} entries
              </p>
            )}

            <ReactPaginate
              previousLabel={<GetIcon iconName="FaAnglesLeft" color="grey" size={13} />}
              nextLabel={<GetIcon iconName="FaAnglesRight" color="grey" size={13} />}
              containerClassName="pagination mb-0"
              breakClassName="page-item"
              activeClassName="active"
              pageClassName="page-item"
              forcePage={currentPage - 1}
              breakLabel="..."
              pageRangeDisplayed={5}
              marginPagesDisplayed={2}
              pageCount={pageCount}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}

      {/* Always visible scrollbar when there's overflow */}
      {hasHorizontalOverflow && (
        <div
          ref={scrollbarTrackRef}
          className="custom-scrollbar"
          style={{
            height: '6px',
            backgroundColor: '#f5f5f5',
            borderRadius: '13px',
            marginTop: '8px',
            marginBottom: '0px',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'pointer',
          }}
        >
          <div
            ref={scrollbarThumbRef}
            className="scrollbar-thumb"
            style={{
              height: '100%',
              backgroundColor: '#ffa500',
              borderRadius: '3px',
              width: '0%',
              position: 'absolute',
              left: 0,
              top: 0,
            }}
          />
        </div>
      )}
      <div style={{ height: '10px', background: 'white' }}></div>

      <style jsx global>{`
        .table-container::-webkit-scrollbar {
          display: none;
        }
        .table-header {
          user-select: none;
        }
        .table-header.sortable:hover {
          background-color: #f5f5f5;
          cursor: pointer;
        }
        .table-header.active-sort {
          background-color: #f0f0f0;
        }
        body.resizing * {
          cursor: col-resize !important;
        }
      `}</style>
    </div>
  );
};

// PropTypes and defaultProps remain the same
ReportTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string.isRequired,
      accessor: PropTypes.string,
      cellRenderer: PropTypes.func,
      sortable: PropTypes.bool,
      width: PropTypes.string,
      style: PropTypes.object,
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  onRowClick: PropTypes.func,
  className: PropTypes.string,
  headerClassName: PropTypes.string,
  rowClassName: PropTypes.string,
  emptyMessage: PropTypes.string,
  loadingMessage: PropTypes.string,
  pageSize: PropTypes.number,
  totalRecords: PropTypes.number,
  currentPage: PropTypes.number,
  onPageChange: PropTypes.func,
  showPagination: PropTypes.bool,
  showPageInfo: PropTypes.bool,
  paginationClassName: PropTypes.string,
};

ReportTable.defaultProps = {
  loading: false,
  showPagination: true,
  showPageInfo: true,
  pageSize: 10,
};

export default ReportTable;